<template>
  <div class="add-final-contract">
    <div class="add-final-contract__content" v-loading="loading">
      <div>
        <div class="add-final-contract__title">
          Создание доходного договора
        </div>
        <div
          class="form-input"
          :class="{
            'form-input--full' : form.Number,
            'form-input--error' : errors.Number
          }"
        >
          <input v-model="form.Number" type="text" required>
          <span class="form-input__label">Номер договора*</span>
          <template v-if="errors.Number && errors.Number.length">
            <div
              v-for="error in errors.Number"
              :key="error"
              class="form-input__error"
             >{{ error }}</div>
          </template>
        </div>
        <div class="form-input">
          <SingleDatePicker
            ref="contractDate"
            class="add-final-contract__date-picker"
            title="Дата договора*"
            :value="form.Date"
            @changeDates="changeDate"
          />
          <template v-if="errors.Date && errors.Date.length">
            <div
              v-for="error in errors.Date"
              :key="error"
              class="form-input__error"
              >{{ error }}</div>
          </template>
        </div>
        <ContractTypeForm 
          :form="form"
          :additionalAgreementOptions="additionalAgreementOptions"
          @changeValue="changeContractTypeForm"
        />
        <div
          class="form-input"
          :class="[{'form-input--full' : form.Amount},{'form-input--error' : errors.Amount}]"
        >
          <input v-model="form.Amount" type="number" required>
          <span class="form-input__label">Сумма договора</span>
          <template v-if="errors.Amount && errors.Amount.length">
            <div
              v-for="error in errors.Amount"
              :key="error"
              class="form-input__error"
             >{{ error }}</div>
          </template>
        </div>
        <Selector
          ref="vat"
          class="add-final-contract__selector add-final-contract__input form-input"
          required
          :title="'с НДС / без НДС'"
          :options="Object.keys(VatType).map((key) => ({ id: key, name: VatType[key] }))"
          :select-fitrst-item="false"
          @handleChange="(val) => form.VatIncluded = val"
        />
      </div>
      <div class="add-final-contract__group-btns">
        <template v-if="errors.global && errors.global.length">
            <div
              v-for="error in errors.global"
              :key="error"
              class="form-input__error"
             >{{ error }}</div>
        </template>
        <div class="add-final-contract__btns-wrapper">
          <ButtonElement
            @click-button="createFinalContract"
            text="Отправить"
            class="add-final-contract__save-btn"
            :disabled="isBtnDisabled"
          />
          <ButtonElement
            @click-button="closeDialog"
            class="add-final-contract__cancel-btn button--empty"
            :text="'Отменить'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ButtonElement from "@/components/elements/ButtonElement";
import ContractTypeForm from "@/components/blocks/ContractTypeForm";
import formValidate from "@/mixin/formValidate";
import Selector from "@/components/sort/Selector";
import numberWithSpaces from '@/mixin/numberWithSpaces'
import { ContractTypeKeys, ContractType, VatType, ContractVariant, AgentActingForPublisherTypeKeys } from "@/utils/contracts";
import SingleDatePicker from "@/components/sort/SingleDatePicker";
import { mapState } from 'vuex'
import moment from 'moment';

const errors = () => ({
  Number: null, 
  Date: null,
  Amount: null,
  VatIncluded: null,
  Type: null,
  SubjectType: null,
  ActionType: null,
  IsAgentActingForPublisher: null,
})

const form = () => ({
  Number: "", 
  Date: null,
  Amount: "",
  VatIncluded: false,
  Type: "",
  SubjectType: "",
  ActionType: null,
  IsAgentActingForPublisher: null,
  ParentMainContractId: null
})

export default {
  name: 'AddFinalContract',
  components: {
    ButtonElement,
    Selector,
    SingleDatePicker,
    ContractTypeForm,
  },
  mixins: [formValidate, numberWithSpaces],
  data() {
    return {
      ContractTypeKeys,
      AgentActingForPublisherTypeKeys,
      ContractVariant,
      ContractType,
      VatType,
      form: form(),
      errors: errors(),
      loading: false,
    }
  },
  computed: {
    ...mapState('Contracts', ['finalContracts']),
    additionalAgreementOptions() {
      return this.finalContracts.map((finalContract) => ({
        id: finalContract.Id,
        name: finalContract.Number
      }))
    },
    isBtnDisabled() {
      if (this.form.Type === ContractTypeKeys.SERVICE_AGREEMENT) {
        return !(this.form.Number && this.form.Type && this.form.SubjectType && this.form.Date)
      }
      if (this.form.Type === ContractTypeKeys.MEDIATION_CONTRACT) {
        return !(
          this.form.Number && 
          this.form.Type && this.form.ActionType && 
          this.form.SubjectType && 
          this.form.Date && 
          this.form.IsAgentActingForPublisher !== null
        )
      }
      if (this.form.Type === ContractTypeKeys.ADDITIONAL_AGREEMENT) {
        return !(this.form.Number && this.form.Type && this.form.ParentMainContractId && this.form.Date)
      }

      return !(this.form.Number && this.form.Type && this.form.Date) 
    }
  },
  methods: {
    createFinalContract() {
      this.loading = true 

      this.cleanErrors()

      const payload = {
        Type: this.form.Type,
        SubjectType: this.form.SubjectType,
        Number: this.form.Number || null, 
        Date: moment(this.form.Date).format('YYYY-MM-DD'),
        VatIncluded: this.form.VatIncluded === 'WITH_VAT',
        ClientId: this.$route.query.clientId,
        ActionType: this.form.ActionType,
        ParentMainContractId: this.form.ParentMainContractId,
        ...(this.form.IsAgentActingForPublisher === AgentActingForPublisherTypeKeys.RR || this.form.IsAgentActingForPublisher === AgentActingForPublisherTypeKeys.RD && { 
          IsAgentActingForPublisher: this.form.IsAgentActingForPublisher === AgentActingForPublisherTypeKeys.RR
        }),
      }

      this.$store.dispatch('Contracts/createFinalContract', payload).then(() => {
        this.cleanData()
        this.$store.commit('Dialog/setData', { label: 'dialogVisible', data: null })
        this.$notify({
          message: this.$t('new_app.success'),
          type: 'success'
        });
        
        this.$store.commit('Dialog/setData', { label: 'activeDialog', data: 'CheckingFinalContract' })
        this.$store.commit('Dialog/setData', { label: 'dialogVisible', data: true })
      }).catch((err) => {
        // const objErrors = JSON.parse(err.data.errors.replace('Mediascout error request:', ''))
        const objErrors = Object.prototype.hasOwnProperty.call(err, 'Mediascout error request') ? JSON.parse(JSON.stringify(err).replace('Mediascout error request:', '')) : JSON.parse(JSON.stringify(err));
        
        objErrors.ErrorItems.map((errorItem) => {
          if (errorItem.PropertyName) {
            this.errors[errorItem.PropertyName] = [
              ...this.errors[errorItem.PropertyName] || [],
              errorItem.ErrorMessage,
            ]
          } else {
            this.errors.global = [...this.errors.global, errorItem.ErrorMessage]
            this.$notify({
              message: errorItem.ErrorMessage,
              type: 'error'
            })
          }
        })
      }).finally(() => {
        this.loading = false
      })
    },
    cleanErrors() {
      this.errors = errors()
    },
    cleanData() {
      this.form = form()
    },
    changeDate(date) {
      this.form.Date = date.startDate
    },
    closeDialog() {
      this.cleanData()
      this.$store.commit('Dialog/setData', { label: 'dialogVisible', data: false })
    },
    changeContractTypeForm({ name, value }) {
      this.form[name] = value
    }
  },
  mounted() {
    
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/helpers/variables";
@import "@/assets/styles/helpers/mixin";

.form-input--full .form-input__label {
    font-size: 1.2rem;
    line-height: 1.4rem;
    top: 0.6rem;
}

.add-final-contract {
  background: #fff;
  color: #000;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &__date-picker {
    width: 100%;
    background: $basicWhite;
  }

  &__input-icon {
    position: absolute;
    right: 1.5rem;
    top: 1.5rem;
  }

  &__group-btns {
    display: flex;
    gap: .8rem;
    flex-direction: column;
  }

  &__btns-wrapper {
    display: flex;
    gap: .8rem;
  }

  &__save-btn {
    width: 17.4rem;
  }

  &__cancel-btn {
    width: 13.7rem;
  }
  
  
  textarea {
    font-family: 'Arial';
  }

  &__title {
    font-weight: 700;
    font-size: 4rem;
    line-height: 4.7rem;
    margin-bottom: 6.5rem;
    color: #000;
  }

  &__content {
    padding: 7rem;
    min-height: 100dvh;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .selector__content {
    background: none;
    border: 1px solid #BFC2C6;
  }

  .selector__title {
    font-size: 1.6rem;
    color: #878B93;
  }
  &__input {
    .selector__content {
      width: 100%;
    }
  }

  .button {
    height: 5rem;
  }

  &__step {
    margin-bottom: 6rem;

    &:last-of-type {
      margin-bottom: 0;
    }

    &-title {
      font-weight: 700;
      font-size: 2.2rem;
      line-height: 2.4rem;
      color: #333333;
      margin-bottom: 2rem;
    }

    &-content {
      .form-input {
        width: 100%;
        max-width: 35rem;

        @include below($sm) {
          max-width: 100%;
        }
      }
    }

    &-error {
      color: $redLight;
      position: absolute;
      left: 6rem;
    }
  }
}

@include below(993px) {
  .add-final-contract__title{
    font-size: 22px;
    line-height: 1.3;
    margin-bottom: 30px;
  }

  .add-final-contract__step{
    margin-bottom: 30px;
  }

  .add-final-contract__final,
  .add-final-contract__content{
    padding: 15px;
  }
}

@include below(769px) {
  .add-final-contract__final{
    .button{
      width: 100%;
      margin-top: 10px;
    }
  }
}
</style>
